// The client's quote page (/q/<token>): the quote or proposal, options, what is included, approve and sign.
import { clientLinkTitle, type PublicProposal, type PublicQuote, quoteTotals } from "@steward/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { quoteFileSrc } from "../../api/client";
import { approveQuote, usePublicQuote } from "../../api/money";
import { CardBox, type CardFields, CardTermsLink, useCardFields } from "../../components/CardFields";
import { moneyExact, shortDate } from "../../components/format";
import { Banner, Button, Skeleton } from "../../components/parts";
import { SignaturePad } from "../../components/SignaturePad";
import { errText } from "../../components/work";
import { AutoQuoteBody } from "../AutoQuote";
import { SectionsShown } from "../QuoteSections";
import { Branded, NotFound } from "./common";

/**
 * The client's quote page. previewId shows it to the office first (owner, 2026-11-03), drafts too: add-ons tick like the
 * client's, but nothing signs, pays or saves.
 */
export function PublicQuotePage({ token, previewId }: { token: string; previewId?: string }) {
  const quote = usePublicQuote(token, previewId);
  const preview = Boolean(previewId);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<string[] | null>(null);
  const [name, setName] = useState("");
  const [drawing, setDrawing] = useState<string | null>(null);
  const [autoPay, setAutoPay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (quote.error) return <NotFound what="quote" />;
  const q = quote.data;
  if (!q)
    return (
      <Branded brand={null}>
        <Skeleton rows={6} />
      </Branded>
    );
  const chosen = picked ?? q.lines.filter((l) => l.optional && l.picked).map((l) => l.id);
  // A proposal's line items list what each includes, under the line.
  const included = (line: string) => q.proposal?.included.find((x) => x.line === line)?.items ?? [];
  // An agency plan (draft 13): a proposal with the 3 month agreement and the auto pay box.
  const plan = q.agreement;
  const open = q.status === "sent" || preview;
  // A job the office booked: the client signs (and saves a card when none is on file) to confirm the day (api jobs/confirm.ts).
  const held = q.confirmFor ?? null;
  const sums = quoteTotals(
    q.lines.map((l) => ({ amountCents: l.amountCents, optional: l.optional, picked: open ? chosen.includes(l.id) : l.picked })),
    q.deposit,
  );
  // One tap: the card first when the quote needs one (Stripe checks it), then the signed approval.
  const approve = async (card: CardFields | null) => {
    if (name.trim().length < 2) {
      setErr("Type your full name to sign.");
      return;
    }
    if (q.signRequired && !drawing) {
      setErr("Sign in the box with your finger or mouse.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (card) qc.setQueryData(["public-quote", token], await card.save());
      const after: PublicQuote = await approveQuote(token, chosen, name.trim(), drawing, autoPay);
      qc.setQueryData(["public-quote", token], after);
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e, "That did not go through. Try again."));
    } finally {
      setBusy(false);
    }
  };
  // An auto quote (a home from the price list, or a lawn): the website's layout, to change it and book a day.
  if (!preview && !q.proposal && !plan && ((open && q.auto) || q.bookedFor))
    return (
      <Branded
        brand={q.brand}
        wide
        account={q.account ? `/my?q=${token}` : undefined}
        title={clientLinkTitle("quote", { brand: q.brand.name, first: q.firstName }).title}
      >
        <AutoQuoteBody token={token} q={q} />
      </Branded>
    );
  return (
    <Branded
      brand={q.brand}
      wide={Boolean(q.proposal)}
      account={q.account ? `/my?q=${token}` : undefined}
      title={
        q.proposal
          ? proposalTabTitle(q.proposal.greeting, q.brand.name)
          : clientLinkTitle("quote", { brand: q.brand.name, first: q.firstName }).title
      }
    >
      {q.proposal ? (
        <ProposalParts p={q.proposal} number={q.number} brand={q.brand.name} totalCents={sums.totalCents} />
      ) : (
        <>
          {held ? (
            <>
              <h1>Hi {q.firstName}, confirm your booking</h1>
              <p>
                <b>
                  {shortDate(held.date)}
                  {held.time ? ` at ${held.time}` : ""}
                </b>
                {q.title ? ` · ${q.title}` : ""}
                {held.often !== "One time" ? `. Then ${held.often.toLowerCase()}.` : ""}
              </p>
              {held.address ? <p className="muted">{held.address}</p> : null}
              {q.home ? <p className="muted">For your home, {q.home}</p> : null}
              <p className="muted">
                {q.hasCard ? "Sign below to confirm. Your card on file stays on file." : "Save a card and sign below to confirm."}
              </p>
            </>
          ) : (
            <>
              <h1>
                Hi {q.firstName}, here's your {plan ? "proposal" : "quote"}
                {q.title ? ` for ${q.title.toLowerCase()}` : ""}
              </h1>
              <p className="muted">
                Quote #{q.number}
                {q.expiresOn && open ? ` · Good until ${shortDate(q.expiresOn)}` : ""}
              </p>
              {q.home ? <p className="muted">For your home, {q.home}</p> : null}
            </>
          )}
        </>
      )}
      <SectionsShown sections={q.sections} files={q.sectionFiles} src={(id) => quoteFileSrc(token, id)} part="intro" />
      {q.message ? <p className="pre-wrap">{q.message}</p> : null}
      {q.signature && plan ? (
        <Banner type="success">
          Signed by {q.signature.name} on {shortDate(q.signature.at.slice(0, 10))}. Thank you. Pay the first month below to get
          started.
        </Banner>
      ) : q.signature && held ? (
        <Banner type="success">
          Confirmed by {q.signature.name} on {shortDate(q.signature.at.slice(0, 10))}. You are on the schedule. See you then.
        </Banner>
      ) : q.signature ? (
        <Banner type="success">
          Approved by {q.signature.name} on {shortDate(q.signature.at.slice(0, 10))}. Thank you. We will reach out to book it.
        </Banner>
      ) : q.status === "expired" ? (
        <Banner type="notice">This quote has expired. Ask us for a new one.</Banner>
      ) : q.status === "declined" ? (
        <Banner type="notice">This quote is closed.</Banner>
      ) : null}
      {q.signature?.drawing ? (
        <img className="sign-shown" src={q.signature.drawing} alt={`Signed by ${q.signature.name}`} />
      ) : null}
      <PriceBox on={Boolean(q.proposal)} monthly={Boolean(q.proposal && !q.proposal.once)}>
        <table className="public-lines">
          <tbody>
            {q.lines
              .filter((l) => !l.optional)
              .map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.description}
                    {l.note ? <span className="muted small"> {l.note}</span> : null}
                    <SeeIncluded link={l.link} />
                    <Included items={included(l.description)} />
                  </td>
                  <td className="num">
                    {moneyExact(l.amountCents)}
                    {l.bill === "monthly" ? " a month" : ""}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        {q.lines.some((l) => l.optionGroup) ? (
          <div className="public-addons" role="radiogroup" aria-label="Pick one">
            <p className="public-label">Pick one</p>
            {q.lines
              .filter((l) => l.optionGroup)
              .map((l) => (
                <label key={l.id} className="public-save">
                  <input
                    type="radio"
                    name="option"
                    disabled={!open}
                    checked={open ? chosen.includes(l.id) : l.picked}
                    onChange={() => setPicked([...chosen.filter((x) => !q.lines.some((o) => o.id === x && o.optionGroup)), l.id])}
                  />
                  <span>
                    <b>{l.optionGroup}</b> {l.description}
                  </span>
                  <span className="num">{moneyExact(l.amountCents)}</span>
                </label>
              ))}
          </div>
        ) : null}
        {q.lines.some((l) => l.optional && !l.optionGroup) ? (
          <div className="public-addons">
            <p className="public-label">Add-ons you can pick</p>
            {q.lines
              .filter((l) => l.optional && !l.optionGroup)
              .map((l) => (
                <label key={l.id} className="public-save">
                  <input
                    type="checkbox"
                    disabled={!open}
                    checked={open ? chosen.includes(l.id) : l.picked}
                    onChange={(e) => setPicked(e.target.checked ? [...chosen, l.id] : chosen.filter((x) => x !== l.id))}
                  />
                  <span>
                    {l.description}
                    {q.proposal && l.note ? <span className="muted small"> {l.note}</span> : null}
                    <Included items={included(l.description)} />
                  </span>
                  <span className="num">{moneyExact(l.amountCents)}</span>
                </label>
              ))}
          </div>
        ) : null}
        <table className="public-lines">
          <tfoot>
            <tr>
              <td>{plan ? "Due when you sign" : held && held.often !== "One time" ? "First visit" : "Total"}</td>
              <td className="num">{moneyExact(plan ? plan.monthlyCents + plan.setupCents : sums.totalCents)}</td>
            </tr>
            {plan ? (
              <tr>
                <td>Then each month</td>
                <td className="num">{moneyExact(plan.monthlyCents)}</td>
              </tr>
            ) : null}
            {sums.depositCents ? (
              <tr>
                <td>Deposit when you approve</td>
                <td className="num">{moneyExact(sums.depositCents)}</td>
              </tr>
            ) : null}
          </tfoot>
        </table>
      </PriceBox>
      <SectionsShown sections={q.sections} files={q.sectionFiles} src={(id) => quoteFileSrc(token, id)} part="rest" />
      {q.proposal?.terms.length ? (
        <section className="pp-section">
          <h2>Terms</h2>
          <div className="pp-terms-box" tabIndex={0} aria-label="Terms">
            <ol className="pp-terms">
              {q.proposal.terms.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ol>
          </div>
          <p className="muted small">Signing below accepts this proposal and these terms as our service agreement.</p>
        </section>
      ) : q.terms ? (
        <details className="public-terms" open={open}>
          <summary>Service agreement</summary>
          <p>{q.terms}</p>
        </details>
      ) : null}
      {plan ? (
        <div className="public-terms">
          <p className="public-label">How the plan works</p>
          <ul>
            {plan.words.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {plan && open ? (
        <label className="public-save">
          <input type="checkbox" checked={autoPay} onChange={(e) => setAutoPay(e.target.checked)} />
          <span>{plan.autoPayWords}</span>
        </label>
      ) : null}
      {preview ? (
        <Banner type="notice">
          Preview. This is what the client sees{q.status === "draft" ? " once you send it" : ""}. Signing is off here.
        </Banner>
      ) : null}
      {open && !preview ? (
        <ApproveBox
          token={token}
          q={q}
          needCard={q.cardRequired && !q.hasCard}
          name={name}
          setName={setName}
          setDrawing={setDrawing}
          err={err}
          busy={busy}
          go={(card) => void approve(card)}
          nameWords={
            q.proposal
              ? "Type your full name to sign"
              : held
                ? "Type your full name to confirm"
                : "Type your full name to approve"
          }
          goWords={plan || q.proposal ? "Sign the proposal" : held ? "Confirm my booking" : "Approve quote"}
          agreeWords={
            q.proposal?.terms.length ? "I agree to the terms above" : q.terms ? "I agree to the service agreement above" : ""
          }
        />
      ) : null}
      {q.payLink && !preview ? (
        <Button
          size="large"
          fullWidth
          onClick={() => void navigate({ to: "/pay/$token", params: { token: q.payLink?.split("/").pop() ?? "" }, search: {} })}
        >
          {plan
            ? `Pay ${moneyExact(plan.monthlyCents + plan.setupCents)} now`
            : `Pay the ${moneyExact(sums.depositCents)} deposit`}
        </Button>
      ) : null}
      {q.proposal ? (
        <Button type="secondary" onClick={() => window.print()}>
          Print or save as PDF
        </Button>
      ) : null}
    </Branded>
  );
}

/** A proposal's tab title: the person's first name when the greeting has one, never the first word of the business. */
function proposalTabTitle(greeting: string, brand: string): string {
  const first = greeting
    .replace(/^Hello\s*/, "")
    .replace(/,$/, "")
    .trim();
  return `${first ? `${first}, your` : "Your"} proposal from ${brand}`;
}

/** The price lines in their own card on a proposal (owner, 2026-11-03), as they were on a plain quote. */
function PriceBox({ on, monthly, children }: { on: boolean; monthly: boolean; children: ReactNode }) {
  if (!on) return <>{children}</>;
  return (
    <section className="pp-section pp-pricebox">
      <h2>{monthly ? "Your monthly price" : "Your price"}</h2>
      {children}
    </section>
  );
}

/**
 * A commercial proposal above the price (the old Steward's steward_proposal_page_html), in the owner's own layout: the
 * cover, a short letter, at a glance, their building, the scope as a checklist, what we bring, insurance, why us, how we
 * keep it right, and what clients say. The page prints as a clean PDF.
 */
function ProposalParts({
  p,
  number,
  brand,
  totalCents,
}: {
  p: PublicProposal;
  number: number;
  brand: string;
  totalCents: number;
}) {
  const check = (items: string[]) => (
    <ul className="pp-check">
      {items.map((x) => (
        <li key={x}>{x}</li>
      ))}
    </ul>
  );
  return (
    <>
      <section className="pp-cover">
        <div className="pp-kicker">{p.serviceWords} proposal</div>
        <h1>{p.building}</h1>
        <div className="pp-meta">
          <span>
            <b>Prepared for</b>
            {p.preparedFor}
            {p.address ? <br /> : null}
            {p.address}
          </span>
          <span>
            <b>Prepared by</b>
            {brand}
            <br />
            {p.date}
          </span>
          <span>
            <b>Proposal</b>#{number}
          </span>
        </div>
      </section>
      {p.letter.length ? (
        <section className="pp-section">
          <p>{p.greeting}</p>
          {p.letter.map((x) => (
            <p key={x}>{x}</p>
          ))}
          <p>
            Best,
            <br />
            <b>{p.signName}</b>
            {p.signTitle ? (
              <>
                <br />
                {p.signTitle}
              </>
            ) : null}
          </p>
        </section>
      ) : null}
      <div className="pp-glance">
        <div>
          <span>Service</span>
          <b>{p.serviceWords}</b>
        </div>
        <div>
          <span>{p.serviceWords === "Construction cleanup" ? "When" : "Schedule"}</span>
          {p.whenLines.length ? (
            <ul className="pp-when">
              {p.whenLines.map((x) => (
                <li key={x.what}>
                  <b>{x.what}</b>
                  <small>{x.how}</small>
                </li>
              ))}
            </ul>
          ) : (
            <b>{p.when || "To be set together"}</b>
          )}
        </div>
        <div className="pp-price">
          <span>{p.once ? "Price" : "Monthly price"}</span>
          <b>{moneyExact(totalCents)}</b>
          {p.once ? null : <small>billed monthly</small>}
        </div>
      </div>
      {p.about ? (
        <section className="pp-section">
          <h2>Your building</h2>
          <p className="pp-pre">{p.about}</p>
        </section>
      ) : null}
      {p.scope.length ? (
        <section className="pp-section">
          <h2>Scope of work</h2>
          {p.scope.map((x) => (
            <div key={x.label}>
              <h3>{x.label}</h3>
              {check(x.items)}
            </div>
          ))}
        </section>
      ) : null}
      {p.bring.length ? (
        <section className="pp-section">
          <h2>What we bring</h2>
          <p className="muted">Everything needed for the job comes with us.</p>
          {check(p.bring)}
        </section>
      ) : null}
      {p.insurance ? (
        <section className="pp-section">
          <h2>Insurance</h2>
          <p>{p.insurance}</p>
        </section>
      ) : null}
      {p.highlights.length ? (
        <section className="pp-section">
          <h2>Why {brand}</h2>
          {check(p.highlights)}
        </section>
      ) : null}
      {p.quality ? (
        <section className="pp-section">
          <h2>How we keep it right</h2>
          <p>{p.quality}</p>
        </section>
      ) : null}
      {p.rating || p.reviews.length ? (
        <section className="pp-section">
          <h2>What clients say</h2>
          {p.rating ? <p className="pp-kicker">{p.rating}</p> : null}
          {p.reviews.map((r) => (
            <blockquote key={r.text} className="pp-review">
              <p>{r.text}</p>
              {r.name ? <cite>{r.name}</cite> : null}
            </blockquote>
          ))}
        </section>
      ) : null}
    </>
  );
}

/**
 * Approving on the quote page, like the old GroundsMen quote page: name, signature, then the card when the quote needs
 * one, and one button that saves the card and approves.
 */
function ApproveBox({
  token,
  q,
  needCard,
  name,
  setName,
  setDrawing,
  err,
  busy,
  go,
  nameWords,
  goWords,
  agreeWords,
}: {
  token: string;
  q: PublicQuote;
  needCard: boolean;
  name: string;
  setName: (v: string) => void;
  setDrawing: (v: string | null) => void;
  err: string | null;
  busy: boolean;
  go: (card: CardFields | null) => void;
  nameWords: string;
  goWords: string;
  /** A box to tick before signing, when there are terms (owner, 2026-11-03). Empty for none. */
  agreeWords: string;
}) {
  const card = useCardFields(token, needCard);
  const [agreed, setAgreed] = useState(false);
  return (
    <div className="public-sign">
      <label className="public-label" htmlFor="sign">
        {nameWords}
      </label>
      <div className="input">
        <input id="sign" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      {q.policies.length ? (
        <div className="public-policies">
          <p className="public-label">By signing, you agree to these policies.</p>
          {q.policies.map((p) => (
            <section key={p.key}>
              <h3>{p.title}</h3>
              <p>{p.text}</p>
            </section>
          ))}
        </div>
      ) : null}
      {q.signRequired ? <SignaturePad onChange={setDrawing} /> : null}
      {needCard ? (
        <CardBox
          card={card}
          title="A card on file books your service. You are not charged now."
          sentence={
            <>
              {q.saveCardSentence} <CardTermsLink />
            </>
          }
        />
      ) : null}
      {agreeWords ? (
        <label className="public-agree">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>{agreeWords}</span>
        </label>
      ) : null}
      {err ? <Banner type="error">{err}</Banner> : null}
      <Button
        size="large"
        fullWidth
        loading={busy}
        disabled={Boolean(agreeWords) && !agreed}
        onClick={() => go(needCard ? card : null)}
      >
        {needCard && goWords === "Approve quote" ? "Approve and save card" : goWords}
      </Button>
    </div>
  );
}

/** What a proposal's line item includes, as a short list under the line. */
function Included({ items }: { items: readonly string[] }) {
  if (!items.length) return null;
  return (
    <span className="public-included">
      <span className="small">What's included</span>
      <ul>
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </span>
  );
}

/** A line's page that lists what is included, from the price list. Opens in a new tab so the quote stays open. */
export function SeeIncluded({ link }: { link: string | null }) {
  if (!link) return null;
  return (
    <a className="see-included small" href={link} target="_blank" rel="noreferrer">
      See what's included
    </a>
  );
}
